import { BLUE_600, GRAY_100, GRAY_200, GRAY_400, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Box, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RoomType, StyleType } from 'constants/visualStaging/visualStagingTypes'

import { Stack } from '@mui/material'
// Import images from room-specific folders
// Balcony/Terrace
import balconyTerraceContemporary from 'assets/img/ai_visual_staging/balcony_terrace/contemporary.webp'
import balconyTerraceIkea from 'assets/img/ai_visual_staging/balcony_terrace/ikea.webp'
import balconyTerraceIndustrial from 'assets/img/ai_visual_staging/balcony_terrace/industrial.webp'
import balconyTerraceLuxury from 'assets/img/ai_visual_staging/balcony_terrace/luxury.webp'
import balconyTerraceModern from 'assets/img/ai_visual_staging/balcony_terrace/modern.webp'
import balconyTerraceScandinavian from 'assets/img/ai_visual_staging/balcony_terrace/scandinavian.webp'
// Bedroom
import bedroomContemporary from 'assets/img/ai_visual_staging/bedroom/contemporary.webp'
import bedroomIkea from 'assets/img/ai_visual_staging/bedroom/ikea.webp'
import bedroomIndustrial from 'assets/img/ai_visual_staging/bedroom/industrial.webp'
import bedroomLuxury from 'assets/img/ai_visual_staging/bedroom/luxury.webp'
import bedroomModern from 'assets/img/ai_visual_staging/bedroom/modern.webp'
import bedroomScandinavian from 'assets/img/ai_visual_staging/bedroom/scandinavian.webp'
// Dining Room
import diningroomContemporary from 'assets/img/ai_visual_staging/diningroom/contemporary.webp'
import diningroomIkea from 'assets/img/ai_visual_staging/diningroom/ikea.webp'
import diningroomIndustrial from 'assets/img/ai_visual_staging/diningroom/industrial.webp'
import diningroomLuxury from 'assets/img/ai_visual_staging/diningroom/luxury.webp'
import diningroomModern from 'assets/img/ai_visual_staging/diningroom/modern.webp'
import diningroomScandinavian from 'assets/img/ai_visual_staging/diningroom/scandinavian.webp'
// Kid Bedroom
import kidBedroomBoyRoom from 'assets/img/ai_visual_staging/kid_bedroom/boy_room.webp'
import kidBedroomChildRoom from 'assets/img/ai_visual_staging/kid_bedroom/child_room.webp'
import kidBedroomGirlRoom from 'assets/img/ai_visual_staging/kid_bedroom/girl_room.webp'
// Kitchen
import kitchenContemporary from 'assets/img/ai_visual_staging/kitchen/contemporary.webp'
import kitchenIkea from 'assets/img/ai_visual_staging/kitchen/ikea.webp'
import kitchenIndustrial from 'assets/img/ai_visual_staging/kitchen/industrial.webp'
import kitchenLuxury from 'assets/img/ai_visual_staging/kitchen/luxury.webp'
import kitchenModern from 'assets/img/ai_visual_staging/kitchen/modern.webp'
import kitchenScandinavian from 'assets/img/ai_visual_staging/kitchen/scandinavian.webp'
// Living Room
import livingroomContemporary from 'assets/img/ai_visual_staging/livingroom/contemporary.webp'
import livingroomIkea from 'assets/img/ai_visual_staging/livingroom/ikea.webp'
import livingroomIndustrial from 'assets/img/ai_visual_staging/livingroom/industrial.webp'
import livingroomLuxury from 'assets/img/ai_visual_staging/livingroom/luxury.webp'
import livingroomModern from 'assets/img/ai_visual_staging/livingroom/modern.webp'
import livingroomScandinavian from 'assets/img/ai_visual_staging/livingroom/scandinavian.webp'
// Office
import officeContemporary from 'assets/img/ai_visual_staging/office/contemporary.webp'
import officeIkea from 'assets/img/ai_visual_staging/office/ikea.webp'
import officeIndustrial from 'assets/img/ai_visual_staging/office/industrial.webp'
import officeLuxury from 'assets/img/ai_visual_staging/office/luxury.webp'
import officeModern from 'assets/img/ai_visual_staging/office/modern.webp'
import officeScandinavian from 'assets/img/ai_visual_staging/office/scandinavian.webp'
// Toilet/Bathroom
import toiletBathroomContemporary from 'assets/img/ai_visual_staging/toilet_bathroom/contemporary.webp'
import toiletBathroomIkea from 'assets/img/ai_visual_staging/toilet_bathroom/ikea.webp'
import toiletBathroomIndustrial from 'assets/img/ai_visual_staging/toilet_bathroom/industrial.webp'
import toiletBathroomLuxury from 'assets/img/ai_visual_staging/toilet_bathroom/luxury.webp'
import toiletBathroomModern from 'assets/img/ai_visual_staging/toilet_bathroom/modern.webp'
import toiletBathroomScandinavian from 'assets/img/ai_visual_staging/toilet_bathroom/scandinavian.webp'
import { useTranslation } from 'react-i18next'

interface StyleOption {
  id: StyleType
  name: string
  thumbnailUrl: string
  category?: string
}

interface StyleSelectorProps {
  /** The currently selected room type for filtering available styles */
  roomType: RoomType | null
  /** The currently selected style for the room */
  selectedStyle: StyleType | null
  /** Callback function called when a style is selected */
  onStyleChange: (style: StyleType) => void
  /** Whether the component should be disabled (e.g., during generation) */
  isDisabled?: boolean
}

/**
 * StyleSelector component for Visual Staging.
 * 2-column grid layout for style selection displayed in the left panel under Room Type.
 * Displays available styles based on selected room type with smooth appear animations.
 * 
 * @example
 * <StyleSelector
 *   roomType={visualStagingState.roomType}
 *   selectedStyle={visualStagingState.selectedStyle}
 *   onStyleChange={changeSelectedStyle}
 *   isDisabled={visualStagingState.isGeneratingStaging}
 * />
 */
export const StyleSelector: React.FC<StyleSelectorProps> = ({
  roomType,
  selectedStyle,
  onStyleChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation(['photo_studio_editing', 'virtual_staging'])
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set())
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const [isVisible, setIsVisible] = useState(false)

  // Image mapping by room type and style type
  const roomStyleImageMap: Record<RoomType, Partial<Record<StyleType, string>>> = useMemo(() => ({
    [RoomType.TERRACE]: {
      [StyleType.CONTEMPORARY]: balconyTerraceContemporary,
      [StyleType.IKEA]: balconyTerraceIkea,
      [StyleType.INDUSTRIAL]: balconyTerraceIndustrial,
      [StyleType.LUXURY]: balconyTerraceLuxury,
      [StyleType.MODERN]: balconyTerraceModern,
      [StyleType.SCANDINAVIAN]: balconyTerraceScandinavian,
    },
    [RoomType.BATHROOM]: {
      [StyleType.CONTEMPORARY]: toiletBathroomContemporary,
      [StyleType.IKEA]: toiletBathroomIkea,
      [StyleType.INDUSTRIAL]: toiletBathroomIndustrial,
      [StyleType.LUXURY]: toiletBathroomLuxury,
      [StyleType.MODERN]: toiletBathroomModern,
      [StyleType.SCANDINAVIAN]: toiletBathroomScandinavian,
    },
    [RoomType.TOILET]: {
      [StyleType.CONTEMPORARY]: toiletBathroomContemporary,
      [StyleType.IKEA]: toiletBathroomIkea,
      [StyleType.INDUSTRIAL]: toiletBathroomIndustrial,
      [StyleType.LUXURY]: toiletBathroomLuxury,
      [StyleType.MODERN]: toiletBathroomModern,
      [StyleType.SCANDINAVIAN]: toiletBathroomScandinavian,
    },
    [RoomType.BEDROOM]: {
      [StyleType.CONTEMPORARY]: bedroomContemporary,
      [StyleType.IKEA]: bedroomIkea,
      [StyleType.INDUSTRIAL]: bedroomIndustrial,
      [StyleType.LUXURY]: bedroomLuxury,
      [StyleType.MODERN]: bedroomModern,
      [StyleType.SCANDINAVIAN]: bedroomScandinavian,
    },
    [RoomType.DINING_ROOM]: {
      [StyleType.CONTEMPORARY]: diningroomContemporary,
      [StyleType.IKEA]: diningroomIkea,
      [StyleType.INDUSTRIAL]: diningroomIndustrial,
      [StyleType.LUXURY]: diningroomLuxury,
      [StyleType.MODERN]: diningroomModern,
      [StyleType.SCANDINAVIAN]: diningroomScandinavian,
    },
    [RoomType.KITCHEN]: {
      [StyleType.CONTEMPORARY]: kitchenContemporary,
      [StyleType.IKEA]: kitchenIkea,
      [StyleType.INDUSTRIAL]: kitchenIndustrial,
      [StyleType.LUXURY]: kitchenLuxury,
      [StyleType.MODERN]: kitchenModern,
      [StyleType.SCANDINAVIAN]: kitchenScandinavian,
    },
    [RoomType.LIVING_ROOM]: {
      [StyleType.CONTEMPORARY]: livingroomContemporary,
      [StyleType.IKEA]: livingroomIkea,
      [StyleType.INDUSTRIAL]: livingroomIndustrial,
      [StyleType.LUXURY]: livingroomLuxury,
      [StyleType.MODERN]: livingroomModern,
      [StyleType.SCANDINAVIAN]: livingroomScandinavian,
    },
    [RoomType.OFFICE]: {
      [StyleType.CONTEMPORARY]: officeContemporary,
      [StyleType.IKEA]: officeIkea,
      [StyleType.INDUSTRIAL]: officeIndustrial,
      [StyleType.LUXURY]: officeLuxury,
      [StyleType.MODERN]: officeModern,
      [StyleType.SCANDINAVIAN]: officeScandinavian,
    },
    [RoomType.KID_BEDROOM]: {
      [StyleType.CHILD_ROOM]: kidBedroomChildRoom,
      [StyleType.BOY_ROOM]: kidBedroomBoyRoom,
      [StyleType.GIRL_ROOM]: kidBedroomGirlRoom,
    },
  }), [])

  // Get style image based on room type and style type
  const getStyleImage = useCallback((roomType: RoomType | null, styleType: StyleType): string => {
    if (!roomType) {
      // Fallback to living room if no room type selected
      return roomStyleImageMap[RoomType.LIVING_ROOM][styleType] || ''
    }
    return roomStyleImageMap[roomType]?.[styleType] || ''
  }, [roomStyleImageMap])

  // Get available styles based on room type
  const getAvailableStyles = useCallback((): StyleOption[] => {
    // Base styles available for all room types
    const allStyles: StyleOption[] = [
      {
        id: StyleType.CONTEMPORARY,
        name: t('virtual_staging.style_selector.contemporary'),
        thumbnailUrl: getStyleImage(roomType, StyleType.CONTEMPORARY),
      },
      {
        id: StyleType.IKEA,
        name: t('virtual_staging.style_selector.ikea'),
        thumbnailUrl: getStyleImage(roomType, StyleType.IKEA),
      },
      {
        id: StyleType.INDUSTRIAL,
        name: t('virtual_staging.style_selector.industrial'),
        thumbnailUrl: getStyleImage(roomType, StyleType.INDUSTRIAL),
      },
      {
        id: StyleType.LUXURY,
        name: t('virtual_staging.style_selector.luxury'),
        thumbnailUrl: getStyleImage(roomType, StyleType.LUXURY),
      },
      {
        id: StyleType.MODERN,
        name: t('virtual_staging.style_selector.modern'),
        thumbnailUrl: getStyleImage(roomType, StyleType.MODERN),
      },
      {
        id: StyleType.SCANDINAVIAN,
        name: t('virtual_staging.style_selector.scandinavian'),
        thumbnailUrl: getStyleImage(roomType, StyleType.SCANDINAVIAN),
      },
      // Kid-specific styles
      {
        id: StyleType.CHILD_ROOM,
        name: t('virtual_staging.style_selector.child_room'),
        thumbnailUrl: getStyleImage(roomType, StyleType.CHILD_ROOM),
      },
      {
        id: StyleType.BOY_ROOM,
        name: t('virtual_staging.style_selector.boy_room'),
        thumbnailUrl: getStyleImage(roomType, StyleType.BOY_ROOM),
      },
      {
        id: StyleType.GIRL_ROOM,
        name: t('virtual_staging.style_selector.girl_room'),
        thumbnailUrl: getStyleImage(roomType, StyleType.GIRL_ROOM),
      },
    ]

    if (!roomType) return []

    // Define room-specific style preferences
    const roomStylePreferences: Record<RoomType, StyleType[]> = {
      [RoomType.LIVING_ROOM]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
      [RoomType.BEDROOM]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
      [RoomType.KID_BEDROOM]: [
        StyleType.CHILD_ROOM, StyleType.BOY_ROOM, StyleType.GIRL_ROOM
      ],
      [RoomType.KITCHEN]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
      [RoomType.DINING_ROOM]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
      [RoomType.BATHROOM]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
      [RoomType.OFFICE]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
      [RoomType.TERRACE]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
      [RoomType.TOILET]: [
        StyleType.CONTEMPORARY, StyleType.MODERN, StyleType.SCANDINAVIAN,
        StyleType.LUXURY, StyleType.IKEA, StyleType.INDUSTRIAL
      ],
    }

    // Get preferred styles for the selected room type
    const preferredStyles = roomStylePreferences[roomType] || Object.values(StyleType)

    // Filter and sort styles based on room preferences
    const filteredStyles = allStyles
      .filter(style => preferredStyles.includes(style.id))
      .sort((a, b) => {
        const aIndex = preferredStyles.indexOf(a.id)
        const bIndex = preferredStyles.indexOf(b.id)
        return aIndex - bIndex
      })

    return filteredStyles
  }, [roomType, t, getStyleImage])

  const availableStyles = getAvailableStyles()

  // Handle style selection
  const handleStyleSelect = useCallback((style: StyleType) => {
    if (isDisabled) return

    // Only call onStyleChange if the style is different from current selection
    if (selectedStyle !== style) {
      onStyleChange(style)
    }
  }, [selectedStyle, onStyleChange, isDisabled])

  // Handle image loading states
  const handleImageLoad = useCallback((styleId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev)
      newSet.delete(styleId)
      return newSet
    })
    setFailedImages(prev => {
      const newSet = new Set(prev)
      newSet.delete(styleId)
      return newSet
    })
  }, [])

  const handleImageError = useCallback((styleId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev)
      newSet.delete(styleId)
      return newSet
    })
    setFailedImages(prev => {
      const newSet = new Set(prev)
      newSet.add(styleId)
      return newSet
    })
  }, [])

  const handleImageLoadStart = useCallback((styleId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev)
      newSet.add(styleId)
      return newSet
    })
  }, [])

  // Reset loading states when room type changes
  useEffect(() => {
    setLoadingImages(new Set())
    setFailedImages(new Set())
    setIsVisible(false)
    // Trigger appear animation after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 100)
    return () => clearTimeout(timer)
  }, [roomType])

  if (!roomType) {
    return null
  }

  return (
    <Stack
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1.2rem',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        pointerEvents: isDisabled ? 'none' : 'auto',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(0.5rem)',
        transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
      }}
    >
      {availableStyles.map((style, index) => (
        <Box
          key={style.id}
          onClick={() => handleStyleSelect(style.id)}
          tabIndex={isDisabled ? -1 : 0}
          role="button"
          aria-label={`${t('virtual_staging.styles.select_style', 'Select style')}: ${style.name}`}
          sx={{
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.6 : 1,
            transition: 'transform 0.2s ease-in-out',
            animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`,
            '@keyframes fadeInUp': {
              '0%': {
                opacity: 0,
                transform: 'translateY(1rem)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
            '&:hover': !isDisabled ? {
              transform: 'translateY(-0.2rem)',
            } : {},
          }}
        >
          {/* Style Thumbnail */}
          <Box
            sx={{
              width: '100%',
              aspectRatio: '4/3',
              borderRadius: '.8rem',
              overflow: 'hidden',
              border: selectedStyle === style.id
                ? `.4rem solid ${BLUE_600}`
                : '.3rem solid transparent',
              transition: 'all 0.2s ease-in-out',
              position: 'relative',
              backgroundColor: GRAY_100,
            }}
          >
            {!failedImages.has(style.id) && (
              <img
                src={style.thumbnailUrl}
                alt={style.name}
                onLoadStart={() => handleImageLoadStart(style.id)}
                onLoad={() => handleImageLoad(style.id)}
                onError={() => handleImageError(style.id)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: loadingImages.has(style.id) ? 'none' : 'block',
                }}
              />
            )}

            {/* Loading placeholder */}
            {loadingImages.has(style.id) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: GRAY_200,
                }}
              >
                <Typography variant="caption" color="text.secondary" fontSize="1rem">
                  {t('virtual_staging.styles.loading')}
                </Typography>
              </Box>
            )}

            {/* Error placeholder */}
            {failedImages.has(style.id) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: GRAY_100,
                  border: `0.1rem dashed ${GRAY_400}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize: '10px' }}>
                  {t('virtual_staging.styles.image_error')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Style Name */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              marginTop: '0.8rem',
              fontSize: '1.2rem',
              fontWeight: selectedStyle === style.id ? 600 : 400,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: selectedStyle === style.id ? GRAY_900 : GRAY_700
            }}
          >
            {style.name}
          </Typography>
        </Box>
      ))}
    </Stack>
  )
}
